import { useQuery } from "@tanstack/react-query";
import { CACHE_KEYS } from "@api/constants/cacheKeys";
import URLS from "@api/constants/urls";
import { handleGlobalGetRequestQuery } from "@api/functions/getRequests";
import { IGetAllServiceProps } from "@api/types/globalService.types";
import { useState } from "react";
import useGlobalDebounceHandler from "./useGlobalDebounceHandler";
import { urlUtility } from "@repo/utilities/url.utility.ts";

export const useGetAllPartnerCertificateService = ({
  page = "1",
  per_page = "15",
  enableQuery = true,
  filters,
  stage_id,
}: IGetAllServiceProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const { debouncedQuery } = useGlobalDebounceHandler(searchQuery);
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const coiStatus = urlQueries?.["coi-status"] || stage_id || "";
  const partnerId = urlQueries?.["partner_id"] || "";
  const segment = urlQueries?.["segment"] || "";
  const allPartnerCertificatesService = useQuery({
    queryKey: [
      ...CACHE_KEYS.CERTIFICATES,
      page,
      coiStatus,
      per_page,
      debouncedQuery,
      partnerId,
      segment,
      "certificate",
    ],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.CERTIFICATES,
        searchParams: {
          "filter[certificate_no]": debouncedQuery,
          "filter[certificate_stage_id]": coiStatus,
          "filter[partner_id]": partnerId,
          "filter[segment_name]": segment,
          ...filters,
          page: page,
          per_page: per_page,
          include: `certificate_stage,enquiry.proposer,enquiry.subjects,enquiry.nominees,partner,certificatable.plans.claimTemplates,certificatable,certificatable.segment${
            partnerId ? ",certificatable.product" : ""
          }`,
        },
      }),
    enabled: enableQuery,
  });
  return {
    services: { allPartnerCertificatesService },
    states: { searchQuery, setSearchQuery },
  };
};

export const useGetSinglePartnerCertificateServices = () => {
  const urlQueries = urlUtility.getUrlQueries(location.search);
  const certificateId =
    urlQueries["certificate-id"] || urlQueries["c-id"] || "";
  const singlePartnerCertificateService = useQuery({
    queryKey: [...CACHE_KEYS.SINGLE_PLAN, certificateId],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `${URLS.CERTIFICATES}/${certificateId}`,
        searchParams: {
          include:
            "certificate_stage,enquiry.proposer,enquiry.subjects,partner,certificatable,certificatable.segment",
        },
      }),
    enabled: certificateId ? true : false,
  });

  return {
    services: { singlePartnerCertificateService },
  };
};
